<template>
  <v-container>
    <v-row
      align="center"
    >
      <v-col cols="auto">
        <v-btn
          icon
          :to="{ name: 'blog' }"
          exact
        >
          <v-icon small>
            fas fa-arrow-left
          </v-icon>
        </v-btn>
      </v-col>
      <v-col
        cols="auto"
      >
        <div class="title text-uppercase font-weight-medium">
          {{ !blogPost ? 'Create new blog post' : 'Edit blog post' }}
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-btn
          color="error"
          text
          class="text-none"
          @click="deleteConfirmationDialogVisible = true"
        >
          <v-icon left>
            fas fa-trash
          </v-icon>
          Delete
        </v-btn>
      </v-col>
    </v-row>

    <v-divider class="mb-10" />

    <validation-observer ref="validation">
      <v-row no-gutters>
        <v-col cols="8">
          <div class="mb-2 font-weight-bold">
            Title
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="dirtyBlogPost.title"
              outlined
              label="Title"
              solo
              :error-messages="errors"
              @change="createSlug"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Excerpt
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-textarea
              v-model="dirtyBlogPost.excerpt"
              outlined
              solo
              label="Excerpt"
              counter
              maxlength="200"
              rows="3"
              :error-messages="errors"
            />
          </validation-provider>

          <div v-if="uploadUrl">
            <div class="mb-2">
              Blog Post Main Cover Image
            </div>
            <validation-provider
              v-slot="{ errors }"
              :rules="{ 'required': !blogPost }"
            >
              <file-uploader
                ref="fileUploader"
                v-model="fileUpload"
                custom-drag-handler="#handleDropUpload"
                accept="image/*"
                auto
                :upload-url="uploadUrl"
                class="mb-6"
                :additional-form-data="{ }"
                @upload="handleMainImageUpload"
              />
              <div
                v-if="errors"
                class="error--text caption"
              >
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <editor
              v-model="dirtyBlogPost.content"
              api-key="10idzplubs6a0oyjggraa93y181syy4du6bsgj41dh3e9nwm"
              :init="{
                plugins: [
                  'advlist', 'anchor', 'autolink', 'codesample', 'fullscreen', 'help',
                  'image', 'tinydrive', 'lists', 'link', 'media', 'preview',
                  'searchreplace', 'table', 'template', 'visualblocks', 'wordcount'
                ],
                toolbar: 'undo redo | accordion accordionremove | blocks fontfamily fontsize | bold italic underline strikethrough | align numlist bullist | link image | table media | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | code fullscreen preview | save print | pagebreak anchor codesample | ltr rtl',
                images_upload_handler: handleEditorImageUpload,
                images_upload_credentials: true,
                height: '600px'
              }"
            />
            <div
              v-if="errors"
              class="error--text caption"
            >
              {{ errors[0] }}
            </div>
          </validation-provider>

          <div class="mt-6 mb-2 font-weight-bold">
            URL Slug (required)
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="dirtyBlogPost.slug"
              outlined
              label="Slug"
              solo
              :error-messages="errors"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Meta Description
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-textarea
              v-model="dirtyBlogPost.meta_description"
              outlined
              solo
              label="Meta Description"
              counter
              maxlength="200"
              rows="3"
              :error-messages="errors"
            />
          </validation-provider>

          <div class="mt-6">
            <v-btn
              x-large
              color="primary"
              :loading="saving"
              @click="storeBlogPost()"
            >
              Save post
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </validation-observer>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :deleting="deleting"
      @confirm="deleteBlogPost()"
    />
  </v-container>
</template>

<script>
// @ is an alias to /src

import BlogPosts from '@/services/BlogPosts'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import Editor from '@tinymce/tinymce-vue'
import FileUploader from '@/components/helpers/FileUploader.vue'
import { mixin as fileAgent } from 'vue-file-agent'
import axios from '@/plugins/axios'
import { kebabCase } from 'lodash'

export default {
  name: 'BlogPost',
  components: {
    FileUploader,
    ConfirmDeleteDialog,
    Editor
  },
  mixins: [deleteConfirmationMixin, fileAgent],
  data() {
    return {
      blogPost: undefined,
      loading: false,
      dirtyBlogPost: {
        title: '',
        excerpt: '',
        content: '',
        image_url: null,
        slug: '',
        meta_description: ''
      },
      fileUpload: [],
      saving: false
    }
  },
  watch: {
    '$route.params.id': function (id) {
      console.log('watch id', id)

      this.fetchBlogPost()
    }
  },
  created() {
    console.log(this.$route.params.id)

    this.fetchBlogPost()

    this.uploadUrl = process.env.VUE_APP_API_URL + '/blog-posts/upload-image'
  },
  methods: {
    async fetchBlogPost() {
      if (!this.$route.params.id) return

      this.loading = true

      try {
        this.blogPost = await BlogPosts.get(this.$route.params.id)

        this.dirtyBlogPost.title = this.blogPost.title
        this.dirtyBlogPost.excerpt = this.blogPost.excerpt
        this.dirtyBlogPost.content = this.blogPost.content
        this.dirtyBlogPost.slug = this.blogPost.slug
        this.dirtyBlogPost.meta_description = this.blogPost.meta_description
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async storeBlogPost() {
      const validation = await this.$refs.validation.validate()

      if (!validation) return

      this.saving = true

      try {
        if (this.blogPost) {
          await BlogPosts.update(this.blogPost.id, this.dirtyBlogPost)
        } else {
          await BlogPosts.store(this.dirtyBlogPost)
        }

        this.$store.dispatch('alert', {
          text: 'Successfully added new blog post',
          color: 'success'
        })

        this.$router.push({ name: 'blog' })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.saving = false
      }
    },
    async deleteBlogPost() {
      this.deleting = true

      try {
        await BlogPosts.delete(this.blogPost.id)

        this.$store.dispatch('alert', {
          text: 'Successfully deleted',
          color: 'success'
        })

        this.$router.push({ name: 'blog' })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    },
    async handleEditorImageUpload(blobInfo, success, failure) {
      const formData = new FormData()
      formData.append('file', blobInfo.blob(), blobInfo.filename())

      try {
        const result = await axios.post(this.uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        console.log(result.data.location)

        success(result.data.location)
      } catch (e) {
        failure(e.message)
      }
    },
    handleMainImageUpload(files) {
      if (!files[0] || !files[0]?.data?.location) {
        this.dirtyBlogPost.image_url = null
      } else {
        this.dirtyBlogPost.image_url = files[0]?.data?.location
      }
    },
    createSlug(title) {
      if (!title || title === '' || this.blogPost) return

      this.dirtyBlogPost.slug = kebabCase(title)
    }
  }
}
</script>
